// Boostt AI · MUI Component Overrides v3.0
// Applies brand tokens to MUI components.
//
// Usage: spread `brandComponentOverrides` into the `components` key of createTheme().
//
// Example:
//   import { brandComponentOverrides } from './brand-theme-overrides';
//   const theme = createTheme({ ..., components: { ...brandComponentOverrides } });

import { ACCENT, LINE, SURFACE, TEXT, MOTION, RADIUS, SHADOW, Z } from './brand-tokens';

export const brandComponentOverrides = {

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 'var(--r-sm)',
        fontWeight: 500,
        fontSize: 13,
        lineHeight: 1.4,
        minHeight: 0,
        textTransform: 'none' as const,
        transition: `all ${MOTION.duration.fast}ms ${MOTION.easing.default}`,
      },
      sizeSmall: {
        fontSize: 12,
        padding: '5px 10px',
        minHeight: 0,
        '& .MuiButton-startIcon': {
          marginLeft: 0,
          marginRight: 6,
        },
      },
      sizeMedium: {
        fontSize: 13,
        padding: '7px 14px',
        minHeight: 0,
      },
      containedPrimary: {
        backgroundColor: 'var(--accent-text)',
        color: 'var(--accent-on-fill)',
        '&:hover': { opacity: 0.9, backgroundColor: 'var(--accent-text)' },
      },
      outlined: {
        borderColor: 'var(--line-strong)',
        color: 'var(--text-primary)',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'var(--bg-hover)',
          borderColor: 'var(--accent-border)',
        },
      },
      outlinedPrimary: {
        borderColor: 'var(--line-strong)',
        color: 'var(--text-primary)',
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'var(--bg-hover)',
          borderColor: 'var(--accent-border)',
        },
      },
      text: {
        color: 'var(--text-primary)',
        '&:hover': { backgroundColor: 'var(--bg-hover)' },
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.md,
        border: `1px solid var(--line)`,
        boxShadow: 'none',
        transition: `border-color ${MOTION.duration.fast}ms ${MOTION.easing.default}`,
        '&:hover': { borderColor: 'var(--line-strong)' },
      },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.md,
        backgroundImage: 'none',
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.xs,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
        height: 'auto',
        padding: '3px 8px',
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: RADIUS.sm,
          fontSize: 13,
          transition: `border-color ${MOTION.duration.fast}ms ${MOTION.easing.default}`,
          '& fieldset': {
            borderColor: theme.palette.mode === 'dark' ? LINE.dark.strong : LINE.light.strong,
          },
          '&:hover fieldset': {
            borderColor: theme.palette.mode === 'dark' ? ACCENT.dark.border : ACCENT.light.border,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.mode === 'dark' ? ACCENT.dark.text : ACCENT.light.text,
            borderWidth: 1,
          },
        },
        '& .MuiInputLabel-root': {
          fontSize: 12,
          fontWeight: 600,
          color: theme.palette.mode === 'dark' ? TEXT.dark.secondary : TEXT.light.secondary,
          '&.Mui-focused': {
            color: theme.palette.mode === 'dark' ? ACCENT.dark.text : ACCENT.light.text,
          },
          '&.MuiInputLabel-shrink': {
            px: '6px',
            ml: '-4px',
            backgroundColor: theme.palette.background.paper,
          },
        },
      }),
    },
  },

  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.sm,
        fontSize: 13,
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS.lg,
        boxShadow: 'var(--shadow-3)',
        border: `1px solid var(--line)`,
      },
    },
    defaultProps: {
      maxWidth: 'sm' as const,
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: '-0.005em',
        padding: '20px 24px 0',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { padding: '16px 24px' },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        padding: '16px 24px',
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? LINE.dark.default : LINE.light.default}`,
        gap: 8,
      }),
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        width: 440,
        borderRadius: 0,
        borderLeft: `1px solid var(--line)`,
        boxShadow: 'var(--shadow-2)',
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: 'var(--bg-active)',
        color: 'var(--text-primary)',
        fontSize: 12,
        fontWeight: 500,
        borderRadius: RADIUS.sm,
        border: `1px solid var(--line-strong)`,
        boxShadow: 'var(--shadow-1)',
        maxWidth: 280,
      },
    },
    defaultProps: {
      arrow: false,
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      root: {
        top: 72,
        right: 24,
        zIndex: Z.toast,
      },
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS.md,
        fontSize: 13,
        fontWeight: 500,
        maxWidth: 400,
        boxShadow: 'var(--shadow-2)',
      },
      standardSuccess: {
        backgroundColor: 'var(--success-bg)',
        border: `1px solid var(--success-border)`,
        color: 'var(--success-text)',
      },
      standardWarning: {
        backgroundColor: 'var(--yellow-bg)',
        border: `1px solid var(--yellow-border)`,
        color: 'var(--yellow-text)',
      },
      standardError: {
        backgroundColor: 'var(--danger-bg)',
        border: `1px solid var(--danger-border)`,
        color: 'var(--danger-text)',
      },
      standardInfo: {
        backgroundColor: 'var(--info-bg)',
        border: `1px solid var(--info-border)`,
        color: 'var(--info-text)',
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }: { theme: any }) => ({
        borderRadius: RADIUS.md,
        border: `1px solid ${theme.palette.mode === 'dark' ? LINE.dark.strong : LINE.light.strong}`,
        boxShadow: theme.palette.mode === 'dark' ? SHADOW.dark[2] : SHADOW.light[2],
        minWidth: 200,
      }),
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        fontSize: 13,
        color: theme.palette.mode === 'dark' ? TEXT.dark.secondary : TEXT.light.secondary,
        borderRadius: RADIUS.sm,
        margin: '2px 4px',
        transition: `all ${MOTION.duration.instant}ms ${MOTION.easing.default}`,
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? SURFACE.dark.hover : SURFACE.light.hover,
          color: theme.palette.mode === 'dark' ? TEXT.dark.primary : TEXT.light.primary,
        },
        '&.Mui-selected': {
          backgroundColor: theme.palette.mode === 'dark' ? ACCENT.dark.bg : ACCENT.light.bg,
          color: theme.palette.mode === 'dark' ? ACCENT.dark.text : ACCENT.light.text,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? ACCENT.dark.bgStrong : ACCENT.light.bgStrong,
          },
        },
      }),
    },
  },

  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: 'var(--text-tertiary)',
          borderBottom: `1px solid var(--line-strong)`,
          backgroundColor: 'var(--bg-app)',
          fontFamily: "'JetBrains Mono', monospace",
        },
      },
    },
  },

  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          transition: `background ${MOTION.duration.instant}ms ${MOTION.easing.default}`,
          '&:hover': { backgroundColor: 'var(--bg-hover)' },
        },
        '& .MuiTableCell-body': {
          fontSize: 13,
          color: 'var(--text-secondary)',
          borderBottom: `1px solid var(--line)`,
          padding: '10px 14px',
        },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { borderBottom: `1px solid var(--line)` },
      indicator: { backgroundColor: 'var(--accent-text)' },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        fontSize: 13,
        fontWeight: 500,
        textTransform: 'none' as const,
        color: 'var(--text-tertiary)',
        minHeight: 44,
        transition: `all ${MOTION.duration.fast}ms ${MOTION.easing.default}`,
        '&:hover': { color: 'var(--text-primary)' },
        '&.Mui-selected': { color: 'var(--accent-text)', fontWeight: 600 },
      },
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 6,
        borderRadius: RADIUS.full,
        backgroundColor: 'var(--bg-active)',
      },
      bar: {
        borderRadius: RADIUS.full,
        backgroundColor: 'var(--accent-text)',
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }: { theme: any }) => ({
        borderRadius: RADIUS.sm,
        backgroundColor: theme.palette.mode === 'dark' ? SURFACE.dark.hover : SURFACE.light.hover,
      }),
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS.md,
        border: `1px solid var(--line-strong)`,
        boxShadow: 'var(--shadow-2)',
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: {
        border: `1px solid var(--line)`,
        borderRadius: `${RADIUS.md}px !important`,
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '&.Mui-expanded': { margin: 0 },
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderColor: 'var(--line)' },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--text-secondary)',
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontSize: 11,
        color: 'var(--text-tertiary)',
        marginTop: 4,
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: { width: 40, height: 22, padding: 0 },
      switchBase: ({ theme }: { theme: any }) => ({
        padding: 3,
        color: theme.palette.mode === 'dark' ? SURFACE.dark.canvas : '#fff',
        '&.Mui-checked': {
          transform: 'translateX(18px)',
          color: theme.palette.mode === 'dark' ? ACCENT.dark.text : ACCENT.light.text,
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.mode === 'dark' ? ACCENT.dark.bgStrong : ACCENT.light.bgStrong,
            border: `1px solid ${theme.palette.mode === 'dark' ? ACCENT.dark.border : ACCENT.light.border}`,
            opacity: 1,
          },
        },
      }),
      thumb: { width: 14, height: 14 },
      track: ({ theme }: { theme: any }) => ({
        borderRadius: RADIUS.full,
        backgroundColor: theme.palette.mode === 'dark' ? SURFACE.dark.active : SURFACE.light.active,
        border: `1px solid ${theme.palette.mode === 'dark' ? LINE.dark.strong : LINE.light.strong}`,
        opacity: 1,
        transition: `all ${MOTION.duration.fast}ms ${MOTION.easing.default}`,
      }),
    },
  },

} as const;
